/**
 * RestoID — Switch account.
 * ---------------------------------------------------------------------------
 * RestoID presentation on the PRISTINE upstream switch-account logic:
 *   - consent info ...... upstream `getConsentInfo` through `useApi` + `useErrorHandler`
 *   - login_hint guard .. upstream `?login_hint=` check -> ErrorPage
 *   - continue .......... upstream navigate to the one-time-token route, `replace: true`,
 *                         carrying the whole query string
 *   - back to current ... upstream `window.location.assign(origin of redirectUri)`
 * Replaces Logto's StaticPageLayout + branding logo + `UserProfile` block with the
 * design's ScreenHead + account card + lime CTA + ghost secondary.
 * Swapped in for `./pages/SwitchAccount` via `src/App.tsx`.
 *
 * Deliberate differences from upstream, and why:
 *  - The tenant branding logo is dropped. In RestoID the logo is the sign-in card's
 *    head (`BrandHead`) and no other screen repeats it. `experienceSettings` is still
 *    read, because the "settings have not loaded yet" half of the guard is upstream's
 *    and stays.
 *  - Upstream's `<LoadingLayer />` (a fixed, full-viewport Logto overlay) becomes a
 *    centred RestoID `Spin` inside the card. Same guard, same condition — only pixels.
 *    It keeps the layout, so `description.switch_account` is on the tab while the
 *    consent info is in flight; upstream's overlay left the previous title standing.
 *  - `UserProfile` is restated as the design's account card: avatar circle, display
 *    name, identifier, lime check. Same fields and the same fallbacks as upstream
 *    (`name ?? description.user_id`, `primaryEmail ?? primaryPhone ?? username`); a
 *    phone identifier goes through the upstream formatter, as on SignInPassword.
 *    The avatar's alt is empty rather than upstream's "avatar": the display name sits
 *    beside it, so announcing the image twice is noise.
 *  - The mock draws the avatar as a lime gradient; here it is the shared lime tile
 *    treatment (`C.tile` / `C.tileLine` / `C.brand`) so the circle stays token-driven
 *    and survives a theme swap — the gradient's dark stop is not a token.
 *  - The mock masks the identifier; this screen does not. It exists so the user can
 *    tell WHICH session they are about to drop, and upstream shows it in full.
 *  - The secondary action keeps upstream's `action.back_to_current_account` rather than
 *    the mock's "use another account" (`c('useAnother')`): the button sends the browser
 *    back to the client app's origin, so the mock's label would name the wrong
 *    destination. Design shape (GhostAction), upstream words — and, as upstream, it
 *    renders even when `redirectUri` is absent and then simply does nothing.
 *  - `description.switch_account_title` is interpolated with the identifier the card
 *    shows (email, else formatted phone, else username) instead of `primaryEmail`
 *    alone, so a phone-only account does not get a sentence with a hole in it, and the
 *    sentence and the card never name the session differently (`resolveIdentifier`).
 *  - No back pill on ScreenHead. Upstream never calls `navigate(-1)` here on purpose:
 *    the history behind this page holds the one-time-token entries, and the upstream
 *    test asserts the escape hatch is a location assign, not a history pop.
 */
import { experience, type ConsentInfoResponse } from '@logto/schemas';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import FirstScreenLayout from '@/Layout/FirstScreenLayout';
import PageContext from '@/Providers/PageContextProvider/PageContext';
import { getConsentInfo } from '@/apis/consent';
import useApi from '@/hooks/use-api';
import useErrorHandler from '@/hooks/use-error-handler';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import { getRedirectUriOrigin } from '@/pages/Consent/util';
import { type Props as PageMetaProps } from '@/shared/components/PageMeta';
import { formatPhoneNumberWithCountryCallingCode } from '@/utils/country-code';

import { Button, C, FONT, GhostAction, Icon, ScreenHead, Spin, Stack } from '../components/restoid';

import ErrorPage from './ErrorPage';

const pageMeta = { titleKey: 'description.switch_account' } satisfies PageMetaProps;

/**
 * How the account names itself — upstream's `UserProfile` fallback chain
 * (`primaryEmail ?? primaryPhone ?? username`), verbatim, with the phone put through
 * the upstream formatter as on SignInPassword. One copy, because the head sentence
 * and the card below it must not disagree about which identifier this session is.
 */
const resolveIdentifier = ({ primaryEmail, primaryPhone, username }: ConsentInfoResponse['user']) =>
  primaryEmail ?? (primaryPhone ? formatPhoneNumberWithCountryCallingCode(primaryPhone) : username);

/**
 * The design's account card — the RestoID restatement of upstream's `UserProfile`.
 * Name/identifier fallbacks are upstream's, verbatim.
 */
const AccountCard = ({ user }: { user: ConsentInfoResponse['user'] }) => {
  const { t } = useTranslation();
  const { id, avatar, name, primaryEmail, username } = user;

  const displayName = name ?? t('description.user_id', { id });
  const identifier = resolveIdentifier(user);
  // Array.from, not [0]: a name may start outside the BMP and charAt would split it.
  const initial = Array.from(name ?? username ?? primaryEmail ?? '')[0]?.toUpperCase() ?? '?';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: 16,
        borderRadius: 12,
        background: C.card,
        border: `1px solid ${C.lineSoft}`,
      }}
    >
      {avatar ? (
        <img
          src={avatar}
          alt=""
          style={{ flex: '0 0 auto', width: 46, height: 46, borderRadius: '50%' }}
        />
      ) : (
        <span
          style={{
            flex: '0 0 auto',
            width: 46,
            height: 46,
            borderRadius: '50%',
            background: C.tile,
            border: `1px solid ${C.tileLine}`,
            color: C.brand,
            display: 'grid',
            placeItems: 'center',
            font: `600 18px/1 ${FONT}`,
          }}
        >
          {initial}
        </span>
      )}
      <span style={{ flex: 1, minWidth: 0 }}>
        <span
          style={{
            display: 'block',
            color: C.text,
            font: `600 15px/1.3 ${FONT}`,
            overflowWrap: 'anywhere',
          }}
        >
          {displayName}
        </span>
        {identifier ? (
          <span
            style={{
              display: 'block',
              marginTop: 2,
              color: C.sub,
              font: `400 13px/1.4 ${FONT}`,
              overflowWrap: 'anywhere',
            }}
          >
            {identifier}
          </span>
        ) : null}
      </span>
      <span style={{ flex: '0 0 auto', display: 'inline-flex', color: C.brand }}>
        <Icon name="check" size={20} />
      </span>
    </div>
  );
};

/**
 * Only reachable when there is an active session and the user is trying to sign in
 * with another account (e.g. through a magic link) — same as upstream.
 */
const SwitchAccount = () => {
  const { t } = useTranslation();
  const { experienceSettings } = useContext(PageContext);
  const navigate = useNavigateWithPreservedSearchParams();
  const handleError = useErrorHandler();

  const [consentData, setConsentData] = useState<ConsentInfoResponse>();
  const asyncGetConsentInfo = useApi(getConsentInfo);

  const [params] = useSearchParams();
  const loginHint = params.get('login_hint');

  useEffect(() => {
    void (async () => {
      const [error, result] = await asyncGetConsentInfo();

      if (error) {
        await handleError(error);
        return;
      }
      setConsentData(result);
    })();
  }, [asyncGetConsentInfo, handleError]);

  if (!loginHint) {
    return <ErrorPage title="error.unknown" message="error.unknown" />;
  }

  if (!experienceSettings || !consentData) {
    return (
      <FirstScreenLayout pageMeta={pageMeta}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '56px 0' }}>
          <Spin size={26} bw={3} />
        </div>
      </FirstScreenLayout>
    );
  }

  const { user, redirectUri } = consentData;
  const redirectUriOrigin = redirectUri ? getRedirectUriOrigin(redirectUri) : undefined;
  const account = resolveIdentifier(user);

  return (
    <FirstScreenLayout pageMeta={pageMeta}>
      <Stack gap={18}>
        <ScreenHead
          title={t('description.switch_account')}
          sub={t('description.switch_account_title', { account })}
        />
        <AccountCard user={user} />
        <p style={{ margin: 0, color: C.sub, font: `400 13px/1.55 ${FONT}` }}>
          {t('description.switch_account_description')}
        </p>
        <Button
          title={t('action.continue_as', { name: loginHint })}
          onClick={() => {
            navigate(
              { pathname: `/${experience.routes.oneTimeToken}`, search: `?${params.toString()}` },
              { replace: true }
            );
          }}
        />
        <GhostAction
          icon="back"
          onClick={() => {
            if (redirectUriOrigin) {
              window.location.assign(redirectUriOrigin);
            }
          }}
        >
          {t('action.back_to_current_account')}
        </GhostAction>
      </Stack>
    </FirstScreenLayout>
  );
};

export default SwitchAccount;
